/**
 * Per-field step bodies for the wizard question flow (027 US6 PR-2, T060;
 * FR-024/FR-026). One catalogue field per step, by type:
 *
 *   - text/textarea → text-step / textarea-step idioms: centred headline (the
 *     field label), the input in a bordered card, the char counter KEPT
 *     (visual-map ledger row 15 — limits are binding data and stay visible).
 *   - date → date-step idiom: typed `pp.kk.vvvv` + the calendar popover.
 *     Typed entry is normalized renderer-side to the engine's ISO
 *     `YYYY-MM-DD` BEFORE it enters the values map (FR-026, `dateText.ts`);
 *     an impossible date shows an error.
 *   - radio/select → card-choice idiom with auto-advance.
 *   - the topic-level attachment → attachment-step idiom: dashed dropzone
 *     carrying the real File bytes (`ComposeAttachment`, `attachmentRef.ts`)
 *     with a blocking type/size pre-check (051), the catalogued hint, always
 *     skippable.
 *
 * Every body is presentation-only and module-scope (R22); the flow machine
 * (`WizardQuestionFlow`) owns values/phase state, the per-step gating
 * (`Seuraava` disabled/enabled), and the PINNED bottom bar — the bodies
 * render inside the flow's `WizardScrollArea` while the action bar stays a
 * static flex child below it (the owner fold at PR-3), so a step body can
 * never hide or overlap the continue action.
 */

import { type JSX, useEffect, useId, useRef, useState } from 'react';

import type { MessageKeys } from '../../../i18n/messages/index.js';
import { useLanguage } from '../../../i18n/useLanguage.js';
import { useTranslate } from '../../../i18n/useTranslate.js';
import {
  attachmentBlockReason,
  MEMBER_ATTACHMENT_MAX_BYTES,
} from '../../../member-cases/attachment-limits.js';
import { formatBytes } from '../../../member-cases/formatBytes.js';
import { Dialog } from '../../../primitives/Dialog.js';
import { Input } from '../../../primitives/Input.js';
import { Textarea } from '../../../primitives/Textarea.js';
import { type ComposeAttachment, toComposeAttachment } from '../attachmentRef.js';
import type { Field, TopicDefinition } from '../MessageComposePort.js';
import { CalendarPopover } from './CalendarPopover.js';
import {
  choiceCardClasses,
  choiceCardSelectedClasses,
  choiceListClasses,
  choiceTitleClasses,
  stepColumnClasses,
} from './chrome.js';
import { formatDateTyping } from './dateText.js';
import { useQuestionFocus } from './useQuestionFocus.js';

const counterClasses = 'self-end text-xs text-muted-foreground';
const hintClasses = 'text-xs text-muted-foreground';
const errorClasses = 'm-0 text-sm font-normal text-destructive';
const inputWrapClasses = 'flex flex-col gap-1 text-left';
const dropzoneClasses =
  'flex w-full cursor-pointer flex-col items-center gap-1 rounded-xl border-2 border-dashed ' +
  'border-primary/40 bg-primary/5 p-6 text-center transition-colors hover:border-primary hover:bg-primary/10';
const dropzoneTitleClasses = 'text-base font-semibold text-primary';
const noteClasses = 'm-0 text-sm text-muted-foreground';

/** Required-field marker, hidden from assistive tech (the input carries aria-required). */
function RequiredMark(): JSX.Element {
  return (
    <span aria-hidden="true" className="ml-0.5 text-destructive">
      *
    </span>
  );
}

export interface TextStepProps {
  readonly field: Field;
  readonly value: string;
  readonly onChange: (fieldName: string, value: string) => void;
}

/** text-step / textarea-step: one input, char counter kept. */
export function TextStep({ field, value, onChange }: TextStepProps): JSX.Element {
  const t = useTranslate();
  const inputId = useId();
  const counterId = `${inputId}-counter`;
  const hasCounter = field.maxLength !== undefined;

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const headingRef = useQuestionFocus(inputRef, textareaRef);

  const shared = {
    id: inputId,
    value,
    required: field.required,
    'aria-required': field.required,
    'aria-describedby': hasCounter ? counterId : undefined,
    maxLength: field.maxLength,
  } as const;

  return (
    <div className={stepColumnClasses}>
      <Dialog.Title size="wizard" ref={headingRef} tabIndex={-1}>
        <label htmlFor={inputId}>
          {t(field.label)}
          {field.required ? <RequiredMark /> : null}
        </label>
      </Dialog.Title>
      <div className={inputWrapClasses}>
        {field.type === 'textarea' ? (
          <Textarea
            {...shared}
            ref={textareaRef}
            rows={6}
            onChange={(event) => onChange(field.name, event.target.value)}
          />
        ) : (
          <Input
            {...shared}
            ref={inputRef}
            type="text"
            onChange={(event) => onChange(field.name, event.target.value)}
          />
        )}
        {hasCounter && field.maxLength !== undefined ? (
          <span id={counterId} aria-live="polite" className={counterClasses}>
            {t('compose.charCounter')
              .replace('{n}', String(value.length))
              .replace('{max}', String(field.maxLength))}
          </span>
        ) : null}
      </div>
    </div>
  );
}

export interface DateStepProps {
  readonly field: Field;
  /** The member's keystrokes (Finnish `pp.kk.vvvv`), owned by the flow. */
  readonly text: string;
  /** The committed engine ISO (`YYYY-MM-DD`) or '' — anchors the calendar. */
  readonly selectedIso: string;
  /** Non-empty text that does not parse to a possible date (FR-026). */
  readonly invalid: boolean;
  readonly onTextChange: (nextText: string) => void;
  /** A calendar pick commits the ISO value AND replaces the typed text. */
  readonly onPick: (iso: string) => void;
}

/**
 * date-step: typed `pp.kk.vvvv` + calendar popover. CONTROLLED: the flow owns
 * the draft text and the parse (it also gates the pinned Seuraava on the
 * result), so this body only renders — the engine never sees a raw Finnish
 * string (FR-026).
 */
export function DateStep({
  field,
  text,
  selectedIso,
  invalid,
  onTextChange,
  onPick,
}: DateStepProps): JSX.Element {
  const t = useTranslate();
  const inputId = useId();
  const hintId = `${inputId}-hint`;
  const errorId = `${inputId}-error`;
  // Pick-focus target: after choosing a day in the calendar, focus lands on
  // THIS input (the field the value landed in) so Enter advances the step
  // instead of re-activating the calendar trigger (owner UX nit follow-up).
  const inputRef = useRef<HTMLInputElement>(null);
  const headingRef = useQuestionFocus(inputRef);

  return (
    <div className={stepColumnClasses}>
      <Dialog.Title size="wizard" ref={headingRef} tabIndex={-1}>
        <label htmlFor={inputId}>
          {t(field.label)}
          {field.required ? <RequiredMark /> : null}
        </label>
      </Dialog.Title>
      <div className={inputWrapClasses}>
        <div className="relative">
          <Input
            ref={inputRef}
            id={inputId}
            type="text"
            inputMode="numeric"
            size="hero"
            value={text}
            placeholder={t('compose.date.placeholder')}
            required={field.required}
            aria-required={field.required}
            aria-invalid={invalid}
            aria-describedby={invalid ? `${hintId} ${errorId}` : hintId}
            onChange={(event) => {
              // The iOS numeric keypad has no dot key, so the dots are
              // inserted for the member. Only with the caret at the end: a
              // mid-text edit passes through so the caret never jumps.
              const { value, selectionStart } = event.target;
              onTextChange(selectionStart === value.length ? formatDateTyping(text, value) : value);
            }}
          />
          <CalendarPopover selectedIso={selectedIso} onPick={onPick} pickFocusRef={inputRef} />
        </div>
        {/* The format moved INTO the field as its placeholder (owner finding
            #5); this sr-only twin keeps it described-by-readable — a
            placeholder alone is not a label, and it vanishes on input. */}
        <span id={hintId} className="sr-only">
          {t('compose.date.placeholder')}
        </span>
        {invalid ? (
          <p id={errorId} className={errorClasses}>
            {t('compose.wizard.date.invalid')}
          </p>
        ) : null}
      </div>
    </div>
  );
}

export interface ChoiceStepProps {
  readonly field: Field;
  /** The committed answer ('' when unanswered): backing onto an answered
   * choice step shows the picked card in the visual-map selected style. */
  readonly selectedValue: string;
  readonly onPick: (fieldName: string, value: string) => void;
}

/** card-choice with auto-advance: picking an option IS the answer + advance. */
export function ChoiceStep({ field, selectedValue, onPick }: ChoiceStepProps): JSX.Element {
  const t = useTranslate();
  // Step autofocus (owner UX nit r2): the first card takes focus so the
  // keyboard path continues — Tab/arrows move, Enter picks. Held-Enter
  // chain-picking is prevented by the flow's repeat guard. R35 effect.
  const firstCardRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    firstCardRef.current?.focus();
  }, []);
  return (
    <div className={stepColumnClasses}>
      <Dialog.Title size="wizard">
        {t(field.label)}
        {field.required ? <RequiredMark /> : null}
      </Dialog.Title>
      <div className={choiceListClasses}>
        {(field.options ?? []).map((option, index) => (
          <button
            key={option.value}
            ref={index === 0 ? firstCardRef : undefined}
            type="button"
            className={
              option.value === selectedValue ? choiceCardSelectedClasses : choiceCardClasses
            }
            onClick={() => onPick(field.name, option.value)}
          >
            <span className={choiceTitleClasses}>{t(option.label)}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

export interface AttachmentStepProps {
  readonly topic: TopicDefinition;
  /**
   * r1 item 7b (D-123): the active per-option attachment hint (i18n key),
   * resolved from the current selection. When present it REPLACES the
   * topic-level hint (e.g. a chosen termination ground, FR-033).
   */
  readonly optionHint?: MessageKeys | undefined;
  readonly attachments: readonly ComposeAttachment[];
  readonly onChange: (attachments: readonly ComposeAttachment[]) => void;
}

/**
 * 051 US3 pre-check: why a selected file was blocked at attach, or null if
 * accepted. The compose picker is single-file (051 clarification 2026-09-21),
 * so there is no per-message count arm here.
 *
 * 055 FR-017: the compose and reply paths now share ONE ceiling
 * (`MEMBER_ATTACHMENT_MAX_BYTES`), closing the former 10 MB / 25 MB split.
 */
function precheckBlockReason(file: File): 'too-large' | 'unsupported' | null {
  // Shared advisory gate (size + type; empty MIME passes to the server sniff).
  return attachmentBlockReason(file, MEMBER_ATTACHMENT_MAX_BYTES);
}

/**
 * attachment-step: dashed dropzone label over the (inherently uncontrolled)
 * native file input; carries the catalogued hint and the accepted-types/size
 * note. A selected file's real bytes are carried through and uploaded to the
 * created case on send (051 US1); an over-size/unsupported file is blocked here
 * (US3). Always skippable — the catalogue only ever allows OPTIONAL attachments
 * (FR-011's gating decided whether this step exists at all).
 */
export function AttachmentStep({
  topic,
  optionHint,
  attachments,
  onChange,
}: AttachmentStepProps): JSX.Element {
  const t = useTranslate();
  const { active } = useLanguage();
  const inputId = useId();
  // The chosen-file announcement (MED-3): a stable-id `role="status"` region
  // associated to the file input via aria-describedby, so assistive tech
  // hears the selection land.
  const selectionId = `${inputId}-selection`;
  const blockId = `${inputId}-block`;
  const acceptedId = `${inputId}-accepted`;
  const selected = attachments[0];
  // 051 US3: the last blocked pick — reason + its filename, so the message can
  // NAME the file (a prior valid file may still be attached, so a generic "this
  // file" would be ambiguous).
  const [block, setBlock] = useState<{
    readonly reason: 'too-large' | 'unsupported';
    readonly fileName: string;
  } | null>(null);
  // r1 7b (D-123): the active option hint, if any, replaces the topic-level hint.
  const hint = optionHint ?? topic.attachments.hint;
  // Step autofocus (owner UX nit r2): the file input takes focus — Space
  // opens the picker, Enter skips onward (the flow's advance). R35 effect.
  const fileInputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    fileInputRef.current?.focus();
  }, []);
  // Name the rejected file so it is unambiguous even when a prior valid file
  // is still attached: "<name>: <reason>".
  const blockMessage =
    block === null
      ? null
      : `${block.fileName}: ${
          block.reason === 'too-large'
            ? t('compose.attachment.blockTooLarge')
            : t('compose.attachment.blockUnsupported')
        }`;
  return (
    <div className={stepColumnClasses}>
      <Dialog.Title size="wizard">{t('compose.wizard.attachments.title')}</Dialog.Title>
      <div className={inputWrapClasses}>
        <label htmlFor={inputId} className={dropzoneClasses}>
          <span className={dropzoneTitleClasses}>{t('compose.wizard.attachments.choose')}</span>
          {hint !== undefined ? <span className={hintClasses}>{t(hint)}</span> : null}
        </label>
        <input
          ref={fileInputRef}
          id={inputId}
          type="file"
          className="sr-only"
          aria-describedby={`${acceptedId} ${selectionId} ${blockId}`}
          {...(block !== null ? { 'aria-invalid': true } : {})}
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file === undefined) {
              setBlock(null);
              onChange([]);
              return;
            }
            // 051 US3: block an over-size / unsupported file AT ATTACH
            // (fix-or-remove); the offending file is not added. The downstream
            // authorities remain the server sniff (type, 415) and the IS (size,
            // 413) — this mirrors them to avoid an avoidable downstream
            // rejection (SC-007). (Count is single-file/latent — no arm here.)
            const reason = precheckBlockReason(file);
            if (reason !== null) {
              setBlock({ reason, fileName: file.name });
              // Do NOT clear the existing selection: a blocked new pick must not
              // silently drop a previously-attached valid file. Only reset the
              // native input so it no longer reports the rejected file.
              event.target.value = '';
              return;
            }
            setBlock(null);
            // 051 US1: carry the real File bytes (uploaded after the case is created).
            onChange([toComposeAttachment(file)]);
          }}
        />
        <p id={selectionId} role="status" className={noteClasses}>
          {selected !== undefined
            ? `${selected.ref.fileName} (${formatBytes(selected.ref.sizeBytes, active, {
                b: t('compose.wizard.attachments.unit.bytes'),
                kb: t('compose.wizard.attachments.unit.kilobytes'),
                mb: t('compose.wizard.attachments.unit.megabytes'),
              })})`
            : ''}
        </p>
        <p id={blockId} role="alert" className={noteClasses}>
          {blockMessage ?? ''}
        </p>
        {/* 051: accepted-types + size hint (was the retired D4/P7 demo note).
            Associated to the input via aria-describedby so a screen-reader user
            hears the allowed types + size ceiling on focus. */}
        <p id={acceptedId} className={noteClasses}>
          {t('compose.attachment.acceptedNote')}
        </p>
      </div>
    </div>
  );
}
